import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { NoIndex } from "@/components/layout/Seo";
import { Spinner } from "@/components/ui";
import { useAuth } from "@/stores/auth";

function FullPageSpinner() {
  return (
    <div className="grid min-h-[60dvh] place-items-center">
      <Spinner className="size-8 text-cyan" label="Loading your session" />
    </div>
  );
}

/** Renders children/outlet only for signed-in users; otherwise sends them to /login?next=… */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === "idle" || status === "loading") return <FullPageSpinner />;
  if (status !== "authenticated") {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return (
    <>
      <NoIndex />
      {children ?? <Outlet />}
    </>
  );
}

/** Auth pages: bounce already signed-in users to their destination. */
export function RequireGuest({ children }: { children?: ReactNode }) {
  const status = useAuth((s) => s.status);
  if (status === "idle" || status === "loading") return <FullPageSpinner />;
  if (status === "authenticated") return <Navigate to="/account" replace />;
  return <>{children ?? <Outlet />}</>;
}
